import { Fragment, useCallback, useMemo, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiOrg } from 'api/org/types/ApiOrg';
import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { Box } from 'components/Box';
import { BulletList } from 'components/BulletList';
import { AnchorButton } from 'components/Button/AnchorButton';
import { Divider } from 'components/Divider';
import { FilestackImage } from 'components/FilestackImage/FilestackImage';
import { Form, ImagePickerField, LogoPickerField } from 'components/Form';
import { Hide } from 'components/Hide';
import { Icon } from 'components/Icon';
import { InfoList } from 'components/InfoList';
import { Link, TrackedLink } from 'components/Link';
import { ListingSignupCta } from 'components/Listing/ListingSignupCta';
import { ListingActionsDropdown } from 'components/ListingActionsDropdown';
import { SaveButton } from 'components/SaveButton';
import { ShareListingButton } from 'components/ShareListingButton';
import { Slideshow } from 'components/Slideshow';
import { H2 } from 'components/Text/H2';
import { Text } from 'components/Text/Text';
import { TruncatedHtmlWithLinks } from 'components/Truncate/TruncatedHtmlWithLinks';
import { TruncatedListings } from 'components/Truncate/TruncatedListings';
import AdminRequestContainer from 'containers/AdminRequestContainer';
import { ClientOnly } from 'containers/ClientOnly';
import { NoAmp } from 'containers/NoAmp';
import { SearchContextHeaderData } from 'containers/SearchContextHeader/SearchContextHeaderData';
import { useModalContainerFunctions } from 'containers/ShowModalContainer';
import { ListingLayoutWrapper } from 'layouts/Listing/ListingLayoutWrapper';
import { ListingPageSection } from 'layouts/Listing/components/ListingPageSection';
import { ListingPageSectionTitle } from 'layouts/Listing/components/ListingPageSectionTitle';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { getCityStateString } from 'utils/address/getCityStateString';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { monthAndYear } from 'utils/date';
import { scrollIntoView } from 'utils/scrollIntoView';

import { Banner } from './components/Banner';
import { OrgPreviewInfo } from './components/OrgPreviewInfo';
import { Toolbar } from './components/Toolbar';

function userOwnsOrg(user: ApiUser, org: ApiOrg): boolean {
  return user?.orgs && user.orgs.some((o) => o.id === org.id);
}

type Props = {
  org: ApiOrg;
  areasOfFocus?: Array<string> | null | undefined;
  searchContext: SearchContextHeaderData | null | undefined;
  isPreview?: boolean;
  isPublished?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit?: (...args: Array<any>) => any;
};

export function ViewOrgPage({
  org,
  areasOfFocus,
  searchContext,
  isPreview,
  isPublished,
  onSubmit,
}: Props) {
  const [editMode, setEditMode] = useState(true);
  const { user } = useUserEnvironment();

  const jobCount = org.jobs ? org.jobs.length : 0;
  const internshipCount = org.internships ? org.internships.length : 0;
  const volopCount = org.volops ? org.volops.length : 0;
  const hasListings = jobCount > 0 || internshipCount > 0 || volopCount > 0;
  const shareListingButton = (
    <ShareListingButton listingType="ORG" listing={org} disabled={isPreview} />
  );
  const listingAddressString = org.address && (
    <>{getCityStateString(org.address)}</>
  );
  // @ts-expect-error TS(2345): Argument of type 'ApiUser | undefined' is not assi... Remove this comment to see the full error message
  const orgOwner = userOwnsOrg(user, org);
  const areas = areasOfFocus || org.areasOfFocus;
  const confirmModal = {
    'data-qa-id': 'confirm-remove-logo',
    type: 'danger',
    title: getText('Remove Image'),
    buttonText: getText('Remove'),
    body: getText('Are you sure you want to remove this image?'),
  };

  const headerTitle = useMemo(() => {
    const hasPhoto = org.slideshowItems.find((item) => item.type === 'IMAGE');
    const hasVideo = org.slideshowItems.find((item) => item.type === 'VIDEO');

    const orgName = org.name;

    if (hasPhoto && !hasVideo) {
      return sprintf(getText('Photos for %(orgName)s'), { orgName });
    }

    if (hasVideo && !hasPhoto) {
      return sprintf(getText('Videos for %(orgName)s'), { orgName });
    }

    return sprintf(getText('Photos & Videos for %(orgName)s'), { orgName });
  }, [org.name, org.slideshowItems]);

  const onImageChange = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name: any,
    image: ApiFilestackImageData | null | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateValue: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    submitForm: any,
  ) => {
    trackClicked(`Org profile: upload ${name}`);
    updateValue(name, image, submitForm);
  };

  const submitHandler = (props: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: Record<string, any>;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleError: (...args: Array<any>) => any;
  }) => {
    if (onSubmit) {
      onSubmit(props);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onClickOpportunities = (e: any) => {
    const element = document.getElementById('opportunities');

    if (element) {
      e.preventDefault();
      scrollIntoView(element);
    }
  };

  const { showModal } = useModalContainerFunctions();
  const openMediaGallery = useCallback(
    ({ itemIndex }: { itemIndex: number }) => {
      // @ts-expect-error TS(2322): Type 'SlideshowData[]' is not assignable to type '... Remove this comment to see the full error message
      showModal('MEDIA_GALLERY', { items: org.slideshowItems, itemIndex });
    },
    [org.slideshowItems, showModal],
  );

  return (
    <ListingLayoutWrapper
      listingType="ORG"
      orgType={org.orgType}
      isHiring={org.hiring}
      searchContext={searchContext}
      urlByLocale={org.url}
      title={org.name}
      subtitle={
        <>
          {listingAddressString}
          {org.links.length > 0 &&
            org.links.map((link) => (
              <Fragment key={link}>
                {' | '}
                <Link href={link} target="_blank" rel="noopener noreferrer">
                  {link}
                </Link>
              </Fragment>
            ))}
        </>
      }
      toolbar={
        orgOwner && (
          <Toolbar
            org={org}
            isPublished={isPublished}
            editMode={editMode}
            setEditMode={setEditMode}
          />
        )
      }
      isAdmin={orgOwner}
      renderActionButtons={(mobile) => (
        <Box display="flex" flexWrap="wrap">
          {hasListings && (
            <Box m="4px">
              <AnchorButton
                href="#opportunities"
                variant="primary"
                onClick={onClickOpportunities}
              >
                {getText('See opportunities')}
              </AnchorButton>
            </Box>
          )}
          <NoAmp>
            <>
              <Box m={1}>
                <SaveButton
                  disabled={isPreview}
                  listingId={org.id}
                  listingType={org.type}
                  listingName={org.name}
                />
              </Box>
              {!mobile && <Box m={1}>{shareListingButton}</Box>}
              <Box m={1}>
                <ListingActionsDropdown mobilePlacement="up" listing={org} />
              </Box>
            </>
          </NoAmp>
        </Box>
      )}
      details={
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          <Box px={0} mb={2}>
            <Box px={0} pb={3} id="about">
              <ListingPageSectionTitle mb="11px">
                {getText('About Us')}
              </ListingPageSectionTitle>
              <TruncatedHtmlWithLinks
                html={org.description}
                truncatedHtml={org.descriptionTruncated}
                showMoreLinkTitle={getText('Read more about this organization')}
                showLessLinkTitle={getText('Read less about this organization')}
              />
            </Box>
            {areas.length > 0 && (
              <ListingPageSection title={getText('Issue Areas Include')}>
                <BulletList
                  columns={2}
                  itemMargin={32}
                  items={areas.map((area) => ({
                    key: area,
                    value: area,
                  }))}
                />
              </ListingPageSection>
            )}
            <ListingPageSection
              title={
                org.phone || org.email
                  ? getText('Location & Contact')
                  : getText('Location')
              }
            >
              <InfoList $columns={1} $noSvgStyles>
                {org.phone && (
                  <li>
                    <Box display="inline-flex" mr="6px">
                      <Icon name="cell-phone" size={24} />
                    </Box>
                    <TrackedLink
                      href={`tel:${org.phone.replace(/[^0-9+]/g, '')}`}
                      eventName="Organization phone number"
                      eventProperties={{
                        method: 'Phone',
                      }}
                    >
                      {org.phone}
                    </TrackedLink>
                  </li>
                )}

                {org.email && (
                  <li>
                    <Box display="inline-flex" mr="6px">
                      <Icon name="mail" size={24} />
                    </Box>

                    <TrackedLink
                      href={`mailto:${org.email}`}
                      target="_blank"
                      eventName="Organization email"
                      eventProperties={{
                        method: 'Email',
                      }}
                    >
                      <Box whiteSpace="nowrap">{org.email}</Box>
                    </TrackedLink>
                  </li>
                )}

                {org.address && (
                  <li>
                    <Box display="inline-flex" mr="6px">
                      <Icon name="location" size={24} />
                    </Box>

                    <div>
                      {org.address.full}
                      {org.address.line2 && <br />}
                      {org.address.line2}
                    </div>
                  </li>
                )}
              </InfoList>
            </ListingPageSection>

            {org.slideshowItems.length > 0 && (
              <ListingPageSection title={headerTitle}>
                <Slideshow
                  carouselId="org-photos-carousel"
                  items={org.slideshowItems}
                  data-qa-id="org-carousel"
                  onClickIndex={openMediaGallery}
                />
              </ListingPageSection>
            )}

            {hasListings && (
              <Box id="opportunities" mb={-38}>
                <ListingPageSection
                  title={sprintf(getText('Current listings by %(orgName)s'), {
                    orgName: org.name,
                  })}
                >
                  {org.jobs.length > 0 && (
                    <Box id="jobs" mb={20}>
                      <TruncatedListings
                        listingType="JOB"
                        listings={org.jobs}
                      />
                    </Box>
                  )}
                  {org.internships.length > 0 && (
                    <Box id="internships" mb={20}>
                      <TruncatedListings
                        listingType="INTERNSHIP"
                        listings={org.internships}
                      />
                    </Box>
                  )}
                  {org.volops.length > 0 && (
                    <Box id="volops" mb={20}>
                      <TruncatedListings
                        listingType="VOLOP"
                        listings={org.volops}
                      />
                    </Box>
                  )}
                </ListingPageSection>
              </Box>
            )}
          </Box>
        </>
      }
      banner={
        ((orgOwner && editMode) || org.banner) && (
          <>
            {isPreview && !isPublished && !org.isFrozen && (
              <OrgPreviewInfo org={org} />
            )}
            <Box
              // @ts-expect-error TS(2774): This condition will always return true since this ... Remove this comment to see the full error message
              height={[userOwnsOrg ? 80 : 35, null, null, 200]}
            >
              {orgOwner && editMode ? (
                <Form
                  analyticsTitle="[main] Org page - banner"
                  usageContext="page"
                  initialValues={{
                    banner: org.banner,
                    id: org.id,
                  }}
                  onSubmit={submitHandler}
                  render={({ updateValue, submitForm }) => (
                    <ImagePickerField
                      name="banner"
                      format="banner"
                      message={getText('Drag a banner image here')}
                      aspectRatio={7.2}
                      altText={org.name}
                      overlayButton
                      onChange={(image) =>
                        onImageChange('banner', image, updateValue, submitForm)
                      }
                      confirmModal={confirmModal}
                      extraAnalyticsData={{
                        context: 'Org Page',
                        file_type: 'Org Banner',
                        file_label: 'Drag a banner image here',
                      }}
                    />
                  )}
                />
              ) : (
                org.banner && (
                  <Banner
                    $handle={org.banner.handle}
                    $mimetype={org.banner.mimetype}
                    data-qa-id="banner-image"
                  />
                )
              )}
            </Box>
          </>
        )
      }
      sidebarImage={
        orgOwner && editMode ? (
          <Box mb={-24}>
            <Form
              analyticsTitle="[main] Org page - sidebar image"
              usageContext="page"
              initialValues={{
                logo: org.logo,
                id: org.id,
              }}
              onSubmit={submitHandler}
              render={({ updateValue, submitForm }) => (
                <LogoPickerField
                  name="logo"
                  altText={org.name}
                  onChange={(image) =>
                    onImageChange('logo', image, updateValue, submitForm)
                  }
                  confirmModal={confirmModal}
                  extraAnalyticsData={{
                    context: 'Org Page',
                    file_type: 'Org Logo',
                    file_label: 'Drag a logo here',
                  }}
                />
              )}
            />
          </Box>
        ) : (
          org.logo && (
            <NoAmp>
              <FilestackImage
                data={org.logo}
                data-qa-id="org-logo"
                width={180}
                height={128}
                alt={sprintf(getText('Logo of %(orgName)s'), {
                  orgName: org.name,
                })}
                noCrop
              />
            </NoAmp>
          )
        )
      }
      sidebarTitle={
        <Box display="flex" flexDirection="column">
          <H2 styleAs="h3" m="0">
            {org.name}
          </H2>
          <Box my="4px">{listingAddressString}</Box>
          <Text
            display={['inline-block', 'inline-block', 'inline-block', 'block']}
            color="lightContentGrey"
          >
            {sprintf(getText('Joined in %(monthAndYear)s'), {
              monthAndYear: monthAndYear(org.created),
            })}
          </Text>
        </Box>
      }
      sidebarHeader={
        <>
          {jobCount > 0 && (
            <Box mb={3}>
              <Link href="#jobs">
                {sprintf(
                  jobCount > 1
                    ? getText('%(jobCount)s current jobs')
                    : getText('%(jobCount)s current job'),
                  {
                    jobCount,
                  },
                )}
              </Link>
            </Box>
          )}
          {internshipCount > 0 && (
            <Box mb={3}>
              <Link href="#internships">
                {sprintf(
                  internshipCount > 1
                    ? getText('%(internshipCount)s current internships')
                    : getText('%(internshipCount)s current internship'),
                  {
                    internshipCount,
                  },
                )}
              </Link>
            </Box>
          )}
          {volopCount > 0 && (
            <Box mb={3}>
              <Link href="#volops">
                {sprintf(
                  volopCount > 1
                    ? getText('%(volopCount)s current volunteer opportunities')
                    : getText('%(volopCount)s current volunteer opportunity'),
                  {
                    volopCount,
                  },
                )}
              </Link>
            </Box>
          )}

          <Hide xs sm>
            {hasListings && (
              <AnchorButton
                onClick={onClickOpportunities}
                href="#opportunities"
                variant="primary"
              >
                {getText('See opportunities')}
              </AnchorButton>
            )}
          </Hide>

          {jobCount > 0 ||
          internshipCount > 0 ||
          volopCount > 0 ||
          hasListings ? (
            <Divider marginTop={24} marginBottom={24} />
          ) : null}
        </>
      }
      sidebarDetails={
        <ClientOnly>
          <AdminRequestContainer
            objectId={org.id}
            objectName={org.name}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            render={({ requestAdmin }: any) => (
              <Text pb={3} color="lightContentGrey">
                {sprintf(getText('Do you work for %(orgName)s?'), {
                  orgName: org.name,
                })}{' '}
                {/* TODO: Fix this the next time the file is edited. */}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    requestAdmin();
                  }}
                >
                  {getText('Become an administrator')}
                </Link>
              </Text>
            )}
          />
        </ClientOnly>
      }
      footer={
        !isPreview &&
        !user && (
          <ListingSignupCta listingUrl={org.url[CURRENT_LOCALE]} type="ORG" />
        )
      }
    />
  );
}
